import { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import MapIcon from '@mui/icons-material/Map';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import GpxExportAction from './GpxExportAction';
import WorkoutGeneratorAction from './WorkoutGeneratorAction';

const MoreActionsPanel = ({ open, onClose, workoutData, gpxFile, storedFile }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selectedAction, setSelectedAction] = useState('workout-generator');
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px

  const actions = [
    {
      id: 'workout-generator',
      label: t('generateWorkoutAction', 'Generate Workout'),
      icon: <FitnessCenterIcon />,
      description: t('generateWorkoutDescription', 'Create a workout file based on your power data'),
      disabled: false
    },
    {
      id: 'gpx-export',
      label: t('exportModifiedGpxAction', 'Export Modified GPX'),
      icon: <MapIcon />,
      description: t('exportModifiedGpxDescription', 'Generate a new GPX file based on current selection'),
      disabled: false
    }
  ];

  const handleActionClick = (actionId) => {
    setSelectedAction(actionId);
    // TODO: Implement action handlers
    console.log('Action clicked:', actionId);
  };

  const renderActionContent = () => {
    if (!selectedAction) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          sx={{ color: colors.grey[400] }}
        >
          <Typography variant="h6">
            {t('selectAnAction', 'Select an action from the list')}
          </Typography>
        </Box>
      );
    }

    // Render specific action content
    switch (selectedAction) {
      case 'workout-generator':
        return <WorkoutGeneratorAction workoutData={workoutData} />;

      case 'gpx-export':
        return <GpxExportAction workoutData={workoutData} />;

      default:
        return null;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMobile ? '100%' : '80%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          backgroundColor: colors.grey[900]
        }
      }}
    >
      <Box display="flex" flexDirection="column" height="100%">
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            backgroundColor: colors.grey[800],
            borderBottom: `1px solid ${colors.grey[700]}`
          }}
        >
          <Typography variant="h4" color={colors.grey[100]} fontWeight="bold">
            {t('moreActions', "Let's train")}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: colors.grey[100] }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexGrow: 1, overflow: 'hidden' }}>
          {/* Actions List */}
          <Box
            sx={{
              width: isMobile ? '100%' : '300px',
              borderRight: isMobile ? 'none' : `1px solid ${colors.grey[700]}`,
              borderBottom: isMobile ? `1px solid ${colors.grey[700]}` : 'none',
              backgroundColor: colors.grey[800],
              flexShrink: 0
            }}
          >
            <List sx={{ py: 0 }}>
              {actions.map((action) => (
                <ListItem key={action.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleActionClick(action.id)}
                    disabled={action.disabled}
                    selected={selectedAction === action.id}
                    sx={{
                      py: isMobile ? 1 : 1.5,
                      px: isMobile ? 1.5 : 2,
                      '&.Mui-selected': {
                        backgroundColor: colors.blueAccent[800],
                        '&:hover': {
                          backgroundColor: colors.blueAccent[700]
                        }
                      },
                      '&:hover': {
                        backgroundColor: colors.grey[700]
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: colors.blueAccent[400], minWidth: isMobile ? '36px' : '40px' }}>
                      {action.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant={isMobile ? "body2" : "body1"} color={colors.grey[100]} sx={{ fontWeight: isMobile ? 500 : 400 }}>
                          {action.label}
                        </Typography>
                      }
                      secondary={!isMobile && (
                        <Typography variant="caption" color={colors.grey[400]}>
                          {action.description}
                        </Typography>
                      )}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Action Content Area */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              backgroundColor: colors.grey[900],
              height: '100%'
            }}
          >
            {renderActionContent()}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MoreActionsPanel;
